import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants';

// ── Types ─────────────────────────────────────────────────

export interface CatalogService {
  id: string;
  name: string;
}

export interface CatalogAttribute {
  id: string;
  name: string;
}

export interface CatalogPricingUnit {
  id: string;
  name: string;
}

export interface CatalogCategory {
  id: string;
  nombre: string;
}

export interface ProviderOffering {
  id: string;
  service_details: CatalogService;
  description: string;
  specifications: Array<{ id?: string; attribute_details: CatalogAttribute; value: string }>;
  costs: Array<{
    id?: string;
    price: number;
    currency: string;
    description?: string;
    pricing_unit_details: CatalogPricingUnit;
  }>;
}

// ── Keys ──────────────────────────────────────────────────

const KEYS = {
  provider: (id: string | undefined) => ['provider', id] as const,
  offerings: (providerId: string | null) => ['offerings', providerId] as const,
  catalogServices: ['catalog-services'] as const,
  pricingUnits: ['pricing-units'] as const,
  attributes: ['attributes'] as const,
  categories: ['categories'] as const,
};

// ── Queries ───────────────────────────────────────────────

export function useProvider(id: string | undefined) {
  return useQuery({
    queryKey: KEYS.provider(id),
    queryFn: async () => {
      const res = await api.get(`${API_ROUTES.providers}${id}/`);
      return res.data;
    },
    enabled: !!id,
  });
}

export function useProviderOfferings(providerId: string | null) {
  return useQuery({
    queryKey: KEYS.offerings(providerId),
    queryFn: async () => {
      const res = await api.get(`${API_ROUTES.offerings}?provider=${providerId}`);
      return (res.data.results || []) as ProviderOffering[];
    },
    enabled: !!providerId,
  });
}

export function useCatalogServices() {
  return useQuery<CatalogService[]>({
    queryKey: KEYS.catalogServices,
    queryFn: async () => {
      const res = await api.get(API_ROUTES.services);
      return Array.isArray(res.data) ? res.data : res.data.results || [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function usePricingUnits() {
  return useQuery<CatalogPricingUnit[]>({
    queryKey: KEYS.pricingUnits,
    queryFn: async () => {
      const res = await api.get(API_ROUTES.pricingUnits);
      return Array.isArray(res.data) ? res.data : res.data.results || [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useCatalogAttributes() {
  return useQuery<CatalogAttribute[]>({
    queryKey: KEYS.attributes,
    queryFn: async () => {
      const res = await api.get(API_ROUTES.attributes);
      return Array.isArray(res.data) ? res.data : res.data.results || [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useCatalogCategories(enabled = true) {
  return useQuery<CatalogCategory[]>({
    queryKey: KEYS.categories,
    queryFn: async () => {
      const res = await api.get(API_ROUTES.categories);
      return Array.isArray(res.data) ? res.data : res.data.results || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Mutations ─────────────────────────────────────────────

export function useCreateService() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { name: string; category_ids?: string[] }) => {
      const res = await api.post(API_ROUTES.services, payload);
      return res.data as CatalogService;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.catalogServices });
      toast.success('Service created');
    },
    onError: () => toast.error('Error creating service'),
  });
}

export function useCreateAttribute() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      const res = await api.post(API_ROUTES.attributes, { name, input_type: 'text' });
      return res.data as CatalogAttribute;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.attributes });
      toast.success('Attribute created');
    },
    onError: () => toast.error('Error creating attribute'),
  });
}

export function useDeleteOffering(providerId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (offeringId: string) => {
      await api.delete(`${API_ROUTES.offerings}${offeringId}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.offerings(providerId) });
      queryClient.invalidateQueries({ queryKey: ['providers'] });
      toast.success('Service removed');
    },
    onError: () => toast.error('Error removing service'),
  });
}
